import React, { useCallback, useEffect, useState } from 'react';
import { Tag, Spin, Button, Tooltip } from 'antd';
import { NdoeResponsiveTable } from '../../components/ndoe';
import {
  ReloadOutlined, CheckCircleOutlined, WarningOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import config from '../../config';
import '../mobileCockpit/ndoeTokens.css';
import './stockReconciliation.css';

// Libellés humains des codes d'anomalie renvoyés par l'API.
const ANOMALY_LABELS = {
  COMPTABLE_NEGATIF: 'Comptable négatif',
  AVARIEES_NEGATIF: 'Avariées négatif',
  AVARIEES_SUP_COMPTABLE: 'Avariées > comptable',
  SUR_ENGAGEMENT: 'Sur-engagement',
};
const GRAVITE_TONE = { critique: 'sr-crit', majeur: 'sr-maj', mineur: 'sr-min' };

const StockReconciliation = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${DOMAIN}/api/journal/reconciliation`);
      setData(res);
    } catch (e) { console.log(e); } finally { setLoading(false); }
  }, [DOMAIN]);

  useEffect(() => { load(); }, [load]);

  const r = data ? data.resume : null;
  const anomalies = data ? data.anomalies : [];

  const columns = [
    {
      title: 'Article', key: 'article', width: 260,
      render: (_, x) => (
        <div className="sr-art">
          <span className="sr-artref">{x.code_article || '—'}</span>
          <span className="sr-artnom">{x.nom_produit}</span>
          <span className="sr-artsub">{x.couleur || '—'} · P{x.taille ?? '?'}</span>
        </div>
      ),
    },
    { title: 'Comptable', dataIndex: 'comptable', align: 'right', width: 92, render: (n) => <span className={`num ${n < 0 ? 'sr-neg' : ''}`}>{n}</span> },
    { title: 'Réservé', dataIndex: 'reserve_total', align: 'right', width: 84, render: (n) => <span className="num">{n}</span> },
    { title: 'Avariées', dataIndex: 'avariees', align: 'right', width: 84, render: (n) => <span className="num">{n}</span> },
    {
      title: <Tooltip title="Comptable − réservé − circulation − avariées, SANS le plancher à 0">Disponible réel</Tooltip>,
      dataIndex: 'disponible_reel', align: 'right', width: 118,
      render: (n) => <span className={`num ${n < 0 ? 'sr-neg' : ''}`}>{n}</span>,
    },
    { title: 'Déficit', dataIndex: 'deficit', align: 'right', width: 84, render: (n) => (n > 0 ? <b className="num sr-neg">−{n}</b> : <span className="sr-nil">—</span>) },
    {
      title: 'Anomalies', key: 'anomalies',
      render: (_, x) => (
        <div className="sr-tags">
          {x.anomalies.map((a) => <Tag key={a} className={`sr-tag ${GRAVITE_TONE[x.gravite] || ''}`}>{ANOMALY_LABELS[a] || a}</Tag>)}
        </div>
      ),
    },
  ];

  return (
    <div className="sr-page">
      <div className="sr-head">
        <span className="sr-eyebrow">Audit stock · intégrité</span>
        <h1>Réconciliation des stocks</h1>
        <p>Contrôle des invariants que l'affichage normal masque (le disponible est plafonné à 0). Lecture seule — rien n'est modifié.</p>
      </div>

      <div className="sr-actions">
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Relancer le contrôle</Button>
        {r && <span className="sr-scanned">{r.total_variantes.toLocaleString('fr-FR')} variantes vérifiées</span>}
      </div>

      <Spin spinning={loading}>
        {r && (
          <div className="sr-cards">
            <div className={`sr-card ${r.sain ? 'sr-card-ok' : 'sr-card-alert'}`}>
              <span className="sr-card-k">Anomalies</span>
              <strong className="num">{r.nb_anomalies.toLocaleString('fr-FR')}</strong>
            </div>
            <div className="sr-card">
              <span className="sr-card-k">Sur-engagement</span>
              <strong className="num">{r.nb_sur_engagement}</strong>
              <small>{r.total_deficit} paire{r.total_deficit > 1 ? 's' : ''} en trop</small>
            </div>
            <div className="sr-card">
              <span className="sr-card-k">Comptable négatif</span>
              <strong className="num">{r.nb_comptable_negatif}</strong>
            </div>
            <div className="sr-card">
              <span className="sr-card-k">Avariées incohérentes</span>
              <strong className="num">{r.nb_avariees_incoherente}</strong>
            </div>
          </div>
        )}

        {r && r.sain ? (
          <div className="sr-ok">
            <SafetyCertificateOutlined className="sr-ok-icon" />
            <div>
              <div className="sr-ok-title"><CheckCircleOutlined /> Tous les stocks sont réconciliés</div>
              <div className="sr-ok-sub">Aucun invariant violé sur {r.total_variantes.toLocaleString('fr-FR')} variantes actives.</div>
            </div>
          </div>
        ) : (
          <>
            {data && data.truncated && (
              <div className="sr-trunc"><WarningOutlined /> Liste tronquée à {data.limite} articles (les plus graves). Corrigez-les puis relancez.</div>
            )}
            <div className="sr-table-wrap">
              {/* Cette page est consultée sur téléphone (arbitrage du
                  propriétaire, 17/07) : sur mobile, liste de cartes plutôt qu'un
                  tableau de 7 colonnes qu'il faut faire glisser. Le `scroll x`
                  reste pour le desktop — sans lui, le cadre .sr-table-wrap
                  (overflow:hidden, posé pour l'arrondi des coins,
                  stockReconciliation.css:38) rogne les colonnes de droite sans
                  recours, aucun ancêtre n'étant scrollable.
                  Carte DÉDUITE des colonnes ci-dessus (lot 3) : leurs rendus
                  n'ont aucune largeur figée, donc rien ne se tronque dans une
                  carte. « Anomalies » — la raison d'être de la page — y devient
                  visible sans manipulation. */}
              <NdoeResponsiveTable rowKey="id_varianteProduit" columns={columns} data={anomalies}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 25, hideOnSinglePage: true, showTotal: (t) => `${t} article${t > 1 ? 's' : ''} en anomalie` }}
                empty={{ icon: '🔎', title: 'Aucune anomalie', text: 'Aucun invariant de stock violé.' }} />
            </div>
          </>
        )}
      </Spin>
    </div>
  );
};

export default StockReconciliation;
